import React, {useCallback, useRef} from 'react';
import {fullImage, imgUrl} from '../../utils/images';
import {Trip} from '../../schema';
import {useGetProfilesQuery, usePatchTripsByTripIdMutation} from '../../store/backendApi';
import config from '../../config';
import {useInlineFieldEditor} from '../../hooks/editTrip/useInlineFieldEditor';
import {useUploadFiles} from '../../utils/upload';
import path from 'path-browserify';
import {CameraIcon, PencilIcon, XMarkIcon} from '@heroicons/react/24/outline';
import {Button} from '../../components/Button';

export interface TripHeaderProps {
  tripData?: Trip;
}

export const EditTripHeader = (props: TripHeaderProps) => {
  const { data: profile } = useGetProfilesQuery();
  const tripData = props.tripData;
  const [patchTrip] = usePatchTripsByTripIdMutation();
  const { uploadFiles } = useUploadFiles();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Header image upload state
  const [uploadingHeader, setUploadingHeader] = React.useState(false);

  // Title editor
  const titleEditor = useInlineFieldEditor({
    initialValue: tripData?.title || '',
    onSave: async (newTitle) => {
      if (!tripData) return;
      await patchTrip({
        tripId: tripData.id,
        partialTripBodyRequest: { title: newTitle },
      }).unwrap();
    },
  });

  // Description editor
  const descriptionEditor = useInlineFieldEditor({
    initialValue: tripData?.description || '',
    onSave: async (newDescription) => {
      if (!tripData) return;
      await patchTrip({
        tripId: tripData.id,
        partialTripBodyRequest: { description: newDescription },
      }).unwrap();
    },
  });

  // Handle header image upload
  const handleHeaderUpload = useCallback(
    async (file: File) => {
      if (!tripData) return;

      setUploadingHeader(true);
      try {
        await uploadFiles({
          tripId: tripData.id,
          files: [file],
          onUploaded: async (fileUploadResult) => {
            if (fileUploadResult.success && fileUploadResult.fileUpload) {
              const headerUrl = fileUploadResult.fileUpload.uploadUrl.pathname;
              const filename = path.basename(headerUrl);
              await patchTrip({
                tripId: tripData.id,
                partialTripBodyRequest: { headerFileName: filename },
              }).unwrap();
            }
          },
        });
      } catch (error) {
        console.error('Header upload failed:', error);
      } finally {
        setUploadingHeader(false);
      }
    },
    [tripData, uploadFiles, patchTrip],
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleHeaderUpload(file);
    }
  };

  if (!tripData) {
    return <div className="min-h-screen bg-canvas" />;
  }

  return (
    <div className="relative h-[65vh] w-full overflow-hidden">
      <img
        src={
          tripData?.headerFileName
            ? imgUrl(tripData.id, fullImage(tripData.headerFileName))
            : '/default-trip-header.jpg'
        }
        alt="Trip header"
        loading="eager"
        // @ts-ignore
        fetchpriority="high"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center 30%' }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-neutral-darkest to-transparent" />

      {/* Camera Button for Header Image */}
      <div className="absolute bottom-4 right-4">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingHeader}
          className="shadow-lg backdrop-blur-sm bg-surface/90 hover:bg-surface"
          iconBefore={
            uploadingHeader ? (
              <div className="h-4 w-4 border-2 border-main border-t-transparent rounded-full animate-spin" />
            ) : (
              <CameraIcon className="h-4 w-4" />
            )
          }
        >
          {uploadingHeader ? 'Uploading...' : ''}
        </Button>
      </div>

      <div className="absolute bottom-0 left-0 w-full px-4 md:px-8 pb-12 pt-24 max-w-7xl mx-auto">
        <div>
          {/* Title - Inline Editable */}
          <div className="mb-6">
            {titleEditor.isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={titleEditor.value}
                  onChange={(e) => titleEditor.handleChange(e.target.value)}
                  onBlur={titleEditor.handleBlur}
                  autoFocus
                  className="block w-full text-4xl md:text-6xl lg:text-7xl  text-on-dark tracking-tight leading-tight
                    bg-neutral-darkest/60 backdrop-blur-sm border-2 border-brand rounded-lg px-4 py-2
                    focus:outline-none focus:ring-2 focus:ring-brand"
                  style={{
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                  }}
                  placeholder="Trip Title"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={titleEditor.cancelEdit}
                    disabled={titleEditor.isSaving}
                    className="backdrop-blur-sm bg-surface/90"
                    iconBefore={<XMarkIcon className="h-4 w-4" />}
                  >
                    Cancel
                  </Button>
                  {titleEditor.isSaving && (
                    <span className="text-sm text-on-brand flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-on-brand border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="group relative inline-block">
                <h1
                  className="text-4xl md:text-6xl lg:text-7xl  text-on-dark tracking-tight leading-tight"
                  style={{
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                  }}
                >
                  {tripData.title}
                </h1>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={titleEditor.startEdit}
                  className="ml-3 shadow-md backdrop-blur-sm bg-surface/80 hover:bg-surface"
                  iconBefore={<PencilIcon className="h-4 w-4" />}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-8">
            {/* Author */}
            <a
              href={`/profile/${tripData.ownerId}`}
              className="flex items-center gap-3 group transition-transform hover:scale-105 origin-left"
            >
              <div className="relative">
                <img
                  src={
                    `${config.media.cdnUrl}/users/${tripData.ownerId}` +
                    `-thumbnail.jpg?v=${Date.now()}`
                  }
                  className="w-14 h-14 rounded-full border-2 border-subtle object-cover shadow-md group-hover:border-surface"
                  alt="Author"
                  loading="eager"
                  onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
                />
              </div>
              <div>
                <span className="block text-xs text-on-brand tracking-wider ">
                  {profile?.username ?? ''}
                </span>
                <span className="text-on-dark ">View Profile</span>
              </div>
            </a>

            {/* Description - Inline Editable */}
            {descriptionEditor.isEditing ? (
              <div className="flex-1 space-y-3">
                <textarea
                  value={descriptionEditor.value}
                  onChange={(e) => descriptionEditor.handleChange(e.target.value)}
                  onBlur={descriptionEditor.handleBlur}
                  autoFocus
                  rows={3}
                  className="block w-full text-lg md:text-xl text-on-brand font-light leading-relaxed
                    bg-neutral-darkest/60 backdrop-blur-sm border-2 border-brand rounded-lg px-4 py-2
                    focus:outline-none focus:ring-2 focus:ring-brand resize-none"
                  placeholder="Trip Description"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={descriptionEditor.cancelEdit}
                    disabled={descriptionEditor.isSaving}
                    className="backdrop-blur-sm bg-surface/90"
                    iconBefore={<XMarkIcon className="h-4 w-4" />}
                  >
                    Cancel
                  </Button>
                  {descriptionEditor.isSaving && (
                    <span className="text-sm text-on-brand flex items-center gap-2">
                      <div className="h-4 w-4 border-2 border-on-brand border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </span>
                  )}
                </div>
              </div>
            ) : tripData.description ? (
              <div className="group relative flex-1">
                <div className="text-lg md:text-xl text-on-brand font-light leading-relaxed max-w-2xl border-l-2 border-subtle pl-4 md:pl-6">
                  {tripData.description}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={descriptionEditor.startEdit}
                  className="mt-2 shadow-md backdrop-blur-sm bg-surface/80 hover:bg-surface"
                  iconBefore={<PencilIcon className="h-4 w-4" />}
                >
                  Edit
                </Button>
              </div>
            ) : (
              <div className="flex-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={descriptionEditor.startEdit}
                  className="shadow-md backdrop-blur-sm bg-surface/80 hover:bg-surface"
                  iconBefore={<PencilIcon className="h-4 w-4" />}
                >
                  Add Description
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};
